// The text a code, CSV or report card shows under a message (#2022).
// A stored artifact's opening text comes from the chat's shared excerpts
// request (artifactExcerpts.ts); one still in the chat's folder, or one the
// server could not excerpt, is downloaded on its own as before.

import { useEffect, useRef, useState } from 'react'
import type { ArtifactInfo } from '../types.js'
import { requestArtifactExcerpt } from '../artifactExcerpts.js'

/** The most of a downloaded file a card keeps. */
const MAX_CONTENT_SIZE = 50_000

export interface ArtifactText {
  text?: string
  error?: string
}

interface Options {
  artifact: ArtifactInfo
  sessionId: string
  apiBaseUrl: string
  authHeader?: string
  /** False for cards that show no text: nothing is fetched. */
  enabled: boolean
}

export function useArtifactText({ artifact, sessionId, apiBaseUrl, authHeader, enabled }: Options): ArtifactText | null {
  const [content, setContent] = useState<ArtifactText | null>(null)
  // Read, not reacted to: the fetch re-runs only when the artifact becomes
  // loadable (it gains an id, or its status changes).
  const latest = useRef({ artifact, sessionId, apiBaseUrl, authHeader })
  latest.current = { artifact, sessionId, apiBaseUrl, authHeader }

  // A freshly registered artifact starts out 'live' with no id, and its file
  // may not be written yet, so the first attempt can 404. It is tried again
  // when it gains an id or turns 'extracted'.
  const fetchKey = `${artifact.id ?? ''}:${artifact.status}`

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const { artifact: art, sessionId: sid, apiBaseUrl: base, authHeader: auth } = latest.current
    const isExtracted = art.status === 'extracted'

    const load = async (): Promise<string> => {
      if (art.id && isExtracted) {
        try {
          const text = await requestArtifactExcerpt({ apiBaseUrl: base, authHeader: auth, sessionId: sid, artifactId: art.id })
          if (text !== null) return text
        } catch {
          // The card fetches its own copy below.
        }
      }
      const url = art.id
        ? `${base}/agent/artifacts/${art.id}/download`
        : `${base}/agent/session/${sid}/workspace/files/${art.filePath.replace(/^\//, '')}`
      const headers: Record<string, string> = {}
      if (auth) headers['Authorization'] = auth
      const res = await fetch(url, { headers })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.text()).slice(0, MAX_CONTENT_SIZE)
    }

    load()
      .then(text => { if (!cancelled) setContent({ text }) })
      .catch(err => {
        if (cancelled) return
        // While the artifact is still being stored a 404 is expected: keep
        // the loading state and let the next status change try again.
        if (!isExtracted) {
          setContent(null)
          return
        }
        setContent({ error: err instanceof Error ? err.message : 'Failed to load' })
      })
    return () => { cancelled = true }
  }, [fetchKey, enabled])

  return content
}
